import '@splidejs/splide/css'
import Splide from '@splidejs/splide'
import {Controller} from 'stimulus'

export default class extends Controller {
  static targets = ['slider', 'thumb', 'scroll', 'modalOverlay', 'modalContent']

  connect() {
    this.slides = []
    this.visibleSlides = new Set()
    this.isDragging = false

    this.slider = new Splide(
      this.sliderTarget,
      {
        classes: {
          pagination: 'tw-hidden',
          arrows: 'tw-hidden'
        },
        type: 'slide',
        focus: 'left',
        autoWidth: true,
        perPage: 3,
        mediaQuery: 'min',
        padding: 20,
        perMove: 3,
        gap: 12,
        breakpoints: {
          1024: {
            padding: 0,
            gap: 24,
            autoWidth: false
          }
        }
      }
    ).mount()

    this.startDrag = this.startDrag.bind(this)
    this.drag = this.drag.bind(this)
    this.endDrag = this.endDrag.bind(this)

    this.initializeIntersectionObserver()
    this.updateThumbPosition()
    this.initializeScrollInteraction()
  }

  initializeIntersectionObserver() {
    this.slides = this.sliderTarget.querySelectorAll('.values__slide')

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const slideIndex = Array.from(this.slides).indexOf(entry.target)

        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          this.visibleSlides.add(slideIndex)
        } else {
          this.visibleSlides.delete(slideIndex)
        }
      })

      this.updateThumbPosition()
    }, {
      root: this.sliderTarget,
      threshold: 0.5
    })

    this.slides.forEach((slide) => {
      this.observer.observe(slide)
    })
  }

  updateThumbPosition() {
    if (this.slides.length === 0 || this.visibleSlides.size === 0) return

    const totalSlides = this.slides.length
    const firstVisibleIndex = Math.min(...this.visibleSlides)
    const lastVisibleIndex = Math.max(...this.visibleSlides)

    const invisibleLeft = firstVisibleIndex
    const invisibleRight = totalSlides - 1 - lastVisibleIndex

    const scrollProgress = invisibleLeft / Math.max(1, invisibleLeft + invisibleRight)
    const thumbPosition = scrollProgress * 100

    this.thumbTarget.style.left = `${Math.min(100, Math.max(0, thumbPosition)) * 0.75}%`
  }

  initializeScrollInteraction() {
    this.scrollTarget.addEventListener('click', (e) => {
      if (e.target === this.thumbTarget || this.isDragging) return

      const rect = this.scrollTarget.getBoundingClientRect()
      const clickX = e.clientX - rect.left
      const percentage = clickX / rect.width
      this.jumpToPosition(percentage)
    })

    this.thumbTarget.addEventListener('mousedown', this.startDrag)
    document.addEventListener('mousemove', this.drag)
    document.addEventListener('mouseup', this.endDrag)

    this.thumbTarget.addEventListener('touchstart', this.startDrag)
    document.addEventListener('touchmove', this.drag)
    document.addEventListener('touchend', this.endDrag)
  }

  startDrag(e) {
    this.isDragging = true
    this.thumbTarget.style.cursor = 'grabbing'
    e.preventDefault()
  }

  drag(e) {
    if (!this.isDragging) return

    const rect = this.scrollTarget.getBoundingClientRect()
    let clientX

    if (e.type.includes('touch')) {
      if (e.touches.length > 0) {
        clientX = e.touches[0].clientX
      } else {
        return
      }
    } else {
      clientX = e.clientX
    }

    const thumbWidth = 0.25
    const x = clientX - (rect.left + rect.width * thumbWidth / 2)
    const percentage = Math.max(0, Math.min(1, x / (rect.width * (1 - thumbWidth))))

    this.jumpToPosition(percentage)
    e.preventDefault()
  }

  endDrag() {
    this.isDragging = false
    this.thumbTarget.style.cursor = 'grab'
  }

  jumpToPosition(percentage) {
    const totalSlides = this.slides.length
    if (totalSlides <= 1) return

    const rect = this.scrollTarget.getBoundingClientRect()
    const targetIndex = Math.round(percentage * (totalSlides - (rect.width >= 720 ? 3 : 1)))

    this.slider.go(targetIndex)
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.slider) {
      this.slider.destroy(true)
    }

    document.removeEventListener('mousemove', this.drag)
    document.removeEventListener('mouseup', this.endDrag)
    document.removeEventListener('touchmove', this.drag)
    document.removeEventListener('touchend', this.endDrag)
  }

  prev() {
    this.slider.go('<')
  }

  next() {
    this.slider.go('>')
  }

  openModal(event) {
    event.preventDefault()
    event.stopPropagation()

    const modalId = event.currentTarget.dataset.valuesModalId
    const modalElement = document.getElementById(modalId)

    if (modalElement) {
      this.modalContentTarget.innerHTML = modalElement.innerHTML
      this.modalOverlayTarget.classList.remove('tw-hidden')
      document.body.classList.add('tw-overflow-hidden')
    }
  }

  closeModal(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    this.modalOverlayTarget.classList.add('tw-hidden')
    this.modalContentTarget.innerHTML = ''
    document.body.classList.remove('tw-overflow-hidden')
  }

  stopPropagation(event) {
    event.stopPropagation()
  }
}
